import { addBreadcrumb } from '@sentry/react';

const WATCHED_FIELD_NAMES = ['username', 'password', 'csrf_token'];
const MAX_STACK_LENGTH = 1500;
const POLL_INTERVAL_MS = 200;
const HOOKED_PROPERTIES = ['value', 'defaultValue', 'type', 'readOnly', 'disabled', 'name'];
const VALUE_PROPERTIES = new Set(['value', 'defaultValue']);
const HOOKED_ATTRIBUTE_METHODS = ['setAttribute', 'removeAttribute', 'toggleAttribute'] as const;
const FIELD_DEFINING_ATTRIBUTES = new Set([
    'value',
    'type',
    'readonly',
    'disabled',
    'name',
    'form',
    'required'
]);
const COSMETIC_ATTRIBUTES = new Set(['class', 'style', 'aria-describedby']);
const FORM_ASSOCIATION_ATTRIBUTES = new Set(['name', 'form']);
const WINDOW_EVENTS = ['blur', 'focus', 'pagehide', 'pageshow'];

type WatchedField = {
    name: string;
    element: HTMLInputElement;
};

type FieldRegistration = {
    name: string;
    isArmed: () => boolean;
};

type FieldSnapshot = {
    connected: boolean;
    inForm: boolean;
    disabled: boolean;
    readOnly: boolean;
    type: string;
    valueLength: number;
    valueAttributeLength: number | null;
    willValidate: boolean;
    valueMissing: boolean;
    matchingFieldsInForm: number;
};

type SnapshotChanges = Record<string, { from: unknown; to: unknown }>;

type StateTracker = {
    reset(): void;
    check(): void;
};

export type FormClearingWatcher = {
    arm(): void;
    disarm(): void;
    recordSnapshot(): void;
};

const fieldRegistrations = new WeakMap<object, FieldRegistration>();
let prototypeHooksInstalled = false;

function captureStack(): string {
    return (new Error().stack ?? '').slice(0, MAX_STACK_LENGTH);
}

function record(message: string, data: Record<string, unknown>): void {
    try {
        addBreadcrumb({ category: 'login.form_clearing', level: 'warning', message, data });
    } catch {
        // Telemetry must never break the submit flow.
    }
}

function armedFieldName(target: unknown): string | null {
    if (typeof target !== 'object' || target === null) {
        return null;
    }

    const registration = fieldRegistrations.get(target);

    return registration?.isArmed() ? registration.name : null;
}

function recordPropertyWrite(target: unknown, property: string, newValue: unknown): void {
    try {
        const field = armedFieldName(target);

        if (field === null) {
            return;
        }

        if (!VALUE_PROPERTIES.has(property)) {
            record('field property set', {
                field,
                property,
                newValue: String(newValue),
                stack: captureStack()
            });
            return;
        }

        if (String(newValue) === '') {
            record('field value cleared', { field, property, stack: captureStack() });
        }
    } catch {
        // Telemetry must never break the submit flow.
    }
}

function recordAttributeWrite(target: unknown, method: string, attribute: unknown): void {
    try {
        const field = armedFieldName(target);
        const attributeName = String(attribute).toLowerCase();

        if (field === null || !FIELD_DEFINING_ATTRIBUTES.has(attributeName)) {
            return;
        }

        record('field attribute written', {
            field,
            attribute: attributeName,
            method,
            stack: captureStack()
        });
    } catch {
        // Telemetry must never break the submit flow.
    }
}

function hookPropertySetter(property: string): void {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, property);
    const originalSetter = descriptor?.set;

    if (!descriptor || !originalSetter) {
        return;
    }

    Object.defineProperty(HTMLInputElement.prototype, property, {
        ...descriptor,
        set(this: HTMLInputElement, newValue: unknown): void {
            recordPropertyWrite(this, property, newValue);
            originalSetter.call(this, newValue);
        }
    });
}

function hookAttributeMethod(method: typeof HOOKED_ATTRIBUTE_METHODS[number]): void {
    const descriptor = Object.getOwnPropertyDescriptor(Element.prototype, method);
    const originalMethod: unknown = descriptor?.value;

    if (!descriptor || typeof originalMethod !== 'function') {
        return;
    }

    Object.defineProperty(Element.prototype, method, {
        ...descriptor,
        value(this: Element, ...args: unknown[]): unknown {
            recordAttributeWrite(this, method, args[0]);

            return Reflect.apply(originalMethod, this, args);
        }
    });
}

/**
 * Hooks the shared prototypes instead of the field instances, so writes through a setter or
 * attribute method obtained from the prototype are caught as well. Only registered fields of an
 * armed watcher are ever recorded.
 */
function installPrototypeHooks(): void {
    if (prototypeHooksInstalled) {
        return;
    }

    prototypeHooksInstalled = true;
    HOOKED_PROPERTIES.forEach(hookPropertySetter);
    HOOKED_ATTRIBUTE_METHODS.forEach(hookAttributeMethod);
}

function recordAttributeMutation(mutation: MutationRecord, fields: WatchedField[]): void {
    const target = mutation.target as Element;
    const attribute = mutation.attributeName ?? '';
    const isWatchedField = fields.some(({ element }) => element === target);
    const isRelevant = isWatchedField
        ? !COSMETIC_ATTRIBUTES.has(attribute)
        : FORM_ASSOCIATION_ATTRIBUTES.has(attribute);

    if (isRelevant) {
        record('field attribute changed', { field: target.getAttribute('name'), attribute });
    }
}

function recordMutations(mutations: MutationRecord[], fields: WatchedField[]): void {
    mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
            recordAttributeMutation(mutation, fields);
            return;
        }

        const removedFields = fields
            .filter(({ element }) =>
                Array.from(mutation.removedNodes).some(
                    (node) => node === element || node.contains(element)
                )
            )
            .map(({ name }) => name);

        if (removedFields.length > 0) {
            record('field removed from form', { fields: removedFields });
        }
    });
}

/** Returns a function that records the mutations still waiting for the observer callback. */
function observeFieldMutations(
    form: HTMLFormElement,
    fields: WatchedField[],
    isArmed: () => boolean
): () => void {
    const handleMutations = (mutations: MutationRecord[]): void => {
        if (isArmed()) {
            recordMutations(mutations, fields);
        }
    };
    const observer = new MutationObserver(handleMutations);

    observer.observe(form, { subtree: true, childList: true, attributes: true });

    return (): void => handleMutations(observer.takeRecords());
}

function readFieldSnapshot(form: HTMLFormElement, { name, element }: WatchedField): FieldSnapshot {
    return {
        connected: element.isConnected,
        inForm: element.form === form,
        disabled: element.disabled,
        readOnly: element.readOnly,
        type: element.type,
        valueLength: element.value.length,
        valueAttributeLength: element.getAttribute('value')?.length ?? null,
        willValidate: element.willValidate,
        valueMissing: element.validity.valueMissing,
        matchingFieldsInForm: form.querySelectorAll(`input[name="${name}"]`).length
    };
}

function diffSnapshots(before: FieldSnapshot | undefined, after: FieldSnapshot): SnapshotChanges {
    if (!before) {
        return {};
    }

    return Object.fromEntries(
        (Object.keys(after) as (keyof FieldSnapshot)[])
            .filter((key) => before[key] !== after[key])
            .map((key) => [key, { from: before[key], to: after[key] }])
    );
}

function createStateTracker(
    form: HTMLFormElement,
    fields: WatchedField[],
    elapsedMs: () => number
): StateTracker {
    const capture = (): Map<string, FieldSnapshot> =>
        new Map(fields.map((field) => [field.name, readFieldSnapshot(form, field)]));
    let previous = new Map<string, FieldSnapshot>();

    return {
        reset: (): void => {
            previous = capture();
        },
        check: (): void => {
            try {
                const current = capture();

                current.forEach((snapshot, field) => {
                    const changes = diffSnapshots(previous.get(field), snapshot);

                    if (Object.keys(changes).length > 0) {
                        record('field state changed', { field, elapsedMs: elapsedMs(), changes });
                    }
                });

                previous = current;
            } catch {
                // Telemetry must never break the submit flow.
            }
        }
    };
}

function listenToPageEvents(
    form: HTMLFormElement,
    fields: WatchedField[],
    elapsedMs: () => number,
    signal: AbortSignal
): void {
    const recordPageEvent = (event: Event): void => {
        record('page event', {
            type: event.type,
            visibilityState: document.visibilityState,
            elapsedMs: elapsedMs()
        });
    };
    const recordFocusChange = (event: Event): void => {
        const field = fields.find(({ element }) => element === event.target);

        if (field) {
            record('field focus changed', {
                field: field.name,
                type: event.type,
                elapsedMs: elapsedMs()
            });
        }
    };

    WINDOW_EVENTS.forEach((type) => window.addEventListener(type, recordPageEvent, { signal }));
    document.addEventListener('visibilitychange', recordPageEvent, { signal });
    form.addEventListener('focusin', recordFocusChange, { signal });
    form.addEventListener('focusout', recordFocusChange, { signal });
}

function findWatchedFields(form: HTMLFormElement): WatchedField[] {
    return WATCHED_FIELD_NAMES.map((name) => ({
        name,
        element: form.querySelector<HTMLInputElement>(`input[name="${name}"]`)
    })).filter((field): field is WatchedField => field.element !== null);
}

/**
 * Diagnostic-only: records how the login fields disappear from the form while the captcha
 * is solving (value cleared by a script, attribute or type change, node removal, or form reset),
 * plus a timeline of field state changes and page events. Records only while armed, and never
 * logs field values.
 */
export default function watchFormClearing(form: HTMLFormElement): FormClearingWatcher {
    let armed = false;
    let armedAt = 0;
    let pollInterval: ReturnType<typeof setInterval> | undefined;
    let pageEventsController: AbortController | null = null;
    const isArmed = (): boolean => armed;
    const elapsedMs = (): number => Math.round(performance.now() - armedAt);

    const fields = findWatchedFields(form);
    installPrototypeHooks();
    fields.forEach(({ name, element }) => fieldRegistrations.set(element, { name, isArmed }));

    const recordPendingMutations = observeFieldMutations(form, fields, isArmed);
    const stateTracker = createStateTracker(form, fields, elapsedMs);
    const catchUp = (): void => {
        recordPendingMutations();
        stateTracker.check();
    };

    form.addEventListener('reset', () => {
        if (armed) {
            record('form reset', { stack: captureStack() });
        }
    });

    return {
        arm: (): void => {
            if (armed) {
                return;
            }

            armed = true;
            armedAt = performance.now();
            stateTracker.reset();
            pageEventsController = new AbortController();
            listenToPageEvents(form, fields, elapsedMs, pageEventsController.signal);
            pollInterval = setInterval(stateTracker.check, POLL_INTERVAL_MS);
        },
        disarm: (): void => {
            if (!armed) {
                return;
            }

            catchUp();
            armed = false;
            clearInterval(pollInterval);
            pageEventsController?.abort();
            pageEventsController = null;
        },
        recordSnapshot: (): void => {
            if (armed) {
                catchUp();
            }

            record(
                'field snapshot at captcha success',
                Object.fromEntries(
                    fields.map((field) => [field.name, readFieldSnapshot(form, field)])
                )
            );
        }
    };
}
